import {ApiClient as Api} from '@hconnect/apiclient'
import {useNotification} from '@hconnect/uikit'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {SlaTrackingQueryKey, UpdateKpiValuesDto, UpdateResultDto} from '../../types'

type ModifySlaKpiValuesParams = {
  plantUpmId: string
}

export const useModifySlaKpiValues = (
  {plantUpmId}: ModifySlaKpiValuesParams,
  options?: UseMutationOptions<UpdateResultDto, AxiosError, UpdateKpiValuesDto>
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {notify} = useNotification()
  const queryClient = useQueryClient()

  const mutationFn: UseMutationOptions<
    UpdateResultDto,
    AxiosError,
    UpdateKpiValuesDto
  >['mutationFn'] = useCallback(
    async (data: UpdateKpiValuesDto) => {
      return (
        await Api.axiosInstance.patch<UpdateResultDto>(
          `/sla-kpi-tracking/api/${plantUpmId}/sla-kpi/tab-values`,
          data
        )
      ).data
    },
    [plantUpmId]
  )

  return useMutation<UpdateResultDto, AxiosError<{message?: string}>, UpdateKpiValuesDto>({
    mutationFn,
    onError: (error) => {
      const errorMessage = error.response?.data?.message ?? t('message.failedToSave')

      notify('error', errorMessage)
    },
    onSuccess: async (...args) => {
      await options?.onSuccess?.(...args)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [SlaTrackingQueryKey.SlaKpiTabValues, plantUpmId]
      })
    },
    ...options
  })
}
